import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  ButtonComponent,
  ButtonSize,
  ButtonType,
  NativeButtonType,
} from '@app/components/button';
import {
  TextAlign,
  TextColor,
  TextComponent,
  TextHeight,
  TextSize,
  TextType,
  TextWeight,
} from '@app/components/text';
import {
  TextInputComponent,
  TextInputDirection,
  TextInputSize,
  TextInputType,
  TextInputWidth,
} from '@app/components/text-input';
import {
  ToastComponent,
  ToastPosition,
  ToastService,
  ToastType,
} from '@app/components/toast';
import { ViewAlign, ViewComponent, ViewDirection } from '@app/components/view';
import { AdminAuthService } from '@app/services/admin-auth/admin-auth.service';
import { AdminBootstrapService } from '@app/services/admin-bootstrap/admin-bootstrap.service';
import { AdminTenantService } from '@app/services/admin-tenant/admin-tenant.service';
import { AdminUserService } from '@app/services/admin-user/admin-user.service';
import { Spacing, StatusCode } from '@app/shared/types';
import { emailRegex, passwordCriteria } from '@app/utils';
import { TranslatePipe } from '@ngx-translate/core';
import { EMPTY, interval, Subscription } from 'rxjs';

const DEFAULT_LOCKED_OUT_MINUTES = 5;
const MIN_CHAR = 2;
const MAX_CHAR = 64;
@Component({
  selector: 'app-sign-in',
  standalone: true,
  templateUrl: './sign-in.component.html',
  styleUrl: './sign-in.component.css',
  imports: [
    TranslatePipe,
    TextInputComponent,
    ButtonComponent,
    CommonModule,
    RouterModule,
    TextComponent,
    ToastComponent,
    ViewComponent,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class SignInComponent implements OnInit, OnDestroy {
  private readonly adminUserService = inject(AdminUserService);
  private readonly adminAuthService = inject(AdminAuthService);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly adminTenantService = inject(AdminTenantService);
  private readonly bootstrapService = inject(AdminBootstrapService);

  email = signal('');
  password = signal('');
  lockedOutMinutesLeft = signal(DEFAULT_LOCKED_OUT_MINUTES);
  minutesLeftMessage = computed(
    () =>
      `${this.lockedOutMinutesLeft()} ${
        this.lockedOutMinutesLeft() ? 'minutes' : 'minute'
      }`
  );
  passwordCriteria = passwordCriteria(this.password, MIN_CHAR, MAX_CHAR);
  isFormValid = computed(() => {
    return Object.values(this.passwordCriteria).every((criteria) => criteria());
  });
  subscriptions!: Subscription;
  authCode: string;
  authId: string;
  isAdminRoleUpgrade: boolean;
  isLockedOut = false;
  invalidPassword = false;
  emailError = false;
  emailErrorMessage = '';
  passwordError = false;
  tenantId: string;
  invalidCredentials = false;
  passwordErrorMessage = '';
  invalidCredentialsMessage = '';
  lockedOutTimeCheck = 10_000; // We are checking if the user is locked out locally every 10s.
  InputSize = TextInputSize;
  InputDirection = TextInputDirection;
  InputWidth = TextInputWidth;
  InputType = TextInputType;
  NativeButtonType = NativeButtonType;
  ButtonSize = ButtonSize;
  ButtonType = ButtonType;
  Spacing = Spacing;
  ViewDirection = ViewDirection;
  ViewAlign = ViewAlign;
  TextAlign = TextAlign;
  TextSize = TextSize;
  TextWeight = TextWeight;
  TextHeight = TextHeight;
  TextColor = TextColor;
  TextType = TextType;
  ToastPosition = ToastPosition;

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit(): void {
    this.subscriptions = new Subscription();
    this.adminAuthService.clearTokenIfExpired();
    this.isLockedOut = this.adminAuthService.isLockedOut();
    if (this.isLockedOut) {
      this.lockedOutMinutesLeft.update(() =>
        this.adminAuthService.getLockedOutTimeLeft()
      );
      this.subscriptions.add(
        interval(this.lockedOutTimeCheck).subscribe(() => {
          const isLockedOut = this.adminAuthService.isLockedOut();
          this.isLockedOut = isLockedOut;
          if (isLockedOut) {
            this.lockedOutMinutesLeft.update(() =>
              this.adminAuthService.getLockedOutTimeLeft()
            );
          } else {
            this.lockedOutMinutesLeft.update(() => DEFAULT_LOCKED_OUT_MINUTES);
          }
        })
      );
    }
    this.subscriptions.add(
      this.activatedRoute.queryParams.subscribe((params) => {
        this.authCode = params['auth-code'];
        this.authId = params['auth-id'];
        this.tenantId = params['tenant-id'];
        this.isAdminRoleUpgrade = !!this.authId;
      })
    );
  }

  submit(e: Event): void {
    e.preventDefault();
    this.emailError = false;
    this.passwordError = false;
    this.invalidCredentials = false;
    this.emailErrorMessage = '';
    this.passwordErrorMessage = '';
    this.invalidCredentialsMessage = '';

    if (!emailRegex.test(this.email())) {
      this.emailError = true;

      if (!this.email()) {
        this.emailErrorMessage = 'Email is required.';
      } else {
        this.emailErrorMessage = 'Email is not valid.';
      }
    }

    if (!this.password()) {
      this.passwordError = true;
      this.passwordErrorMessage = 'Password is required.';
    }

    if (this.emailError || this.passwordError) {
      return;
    }

    this.adminTenantService.clearTenant();

    this.subscriptions.add(
      this.adminAuthService
        .login(this.email(), this.password(), this.isAdminRoleUpgrade)
        .subscribe(async (res) => {
          if (this.isAdminRoleUpgrade) {
            const userId = res.data.id;
            if (this.isFormValid()) {
              return this.adminUserService
                .addAdminRole(this.tenantId, this.authId, userId)
                .subscribe(() => {
                  this.router.navigateByUrl('/');
                  return EMPTY;
                });
            } else {
              this.router.navigateByUrl(
                `/join/${this.authId}/${this.authCode}/${userId}/${this.tenantId}`
              );
              return EMPTY;
            }
          }

          if (res.code === StatusCode.OK) {
            this.bootstrapService.initializeApp().subscribe({
              next: () => {
                void this.router.navigate([
                  this.adminTenantService.getDefaultLandingRoute(),
                ]);
              },
              error: () => {
                this.adminAuthService.signOutAndRedirect();
              },
            });
          } else if (res.code === StatusCode.TOO_MANY_REQUESTS) {
            this.isLockedOut = true;
            if (this.isLockedOut) {
              interval(this.lockedOutTimeCheck).subscribe(() => {
                const isLockedOut = this.adminAuthService.isLockedOut();
                this.isLockedOut = isLockedOut;
                if (isLockedOut) {
                  this.lockedOutMinutesLeft.update(() =>
                    this.adminAuthService.getLockedOutTimeLeft()
                  );
                } else {
                  this.lockedOutMinutesLeft.update(
                    () => DEFAULT_LOCKED_OUT_MINUTES
                  );
                }
              });
            }
          } else if (res.code === StatusCode.UNAUTHORIZED) {
            this.invalidCredentials = true;
            this.invalidCredentialsMessage = 'Incorrect email or password.';
          } else if (res.code === StatusCode.FORBIDDEN) {
            this.invalidCredentials = true;
            this.invalidCredentialsMessage =
              'You do not have permission to access this application.';
            /**
             * This should not technically happen as we are using validation on the frontend.
             * We are, however, keeping this as a safe-guard against future changes that might
             * happen. If so, we will notice it here.
             */
          } else if (res.code === StatusCode.BAD_REQUEST) {
            this.invalidCredentials = true;
            this.invalidCredentialsMessage =
              'Please make sure that your email and password are formatted correctly.';
          } else if (res.code === StatusCode.INTERNAL_SERVER_ERROR) {
            this.toastService.add(
              'Seems like there is an issue with the server. Please try again later.',
              ToastType.ERROR
            );
          } else {
            this.toastService.add(
              'Seems like there is an issue logging in. Please try again later.',
              ToastType.ERROR
            );
          }
        })
    );
  }
}
